import { Base } from '@studiometa/js-toolkit';
import { debounce } from '@studiometa/js-toolkit/utils';
import Choices from 'choices.js';

/**
 * @typedef {object} SelectInterface
 * @property {HTMLSelectElement} $el
 */
/**
 * Select component
 */
export default class Autocomplete extends Base {
  /**
   * Component Configuration
   */
  static config = {
    name: 'Autocomplete',
    options: {
      endpoint: {
        type: String,
        default: '',
      },
      all: {
        type: String,
        default: '',
      },
      resultstext: {
        type: String,
        default: '',
      },
      choicestext: {
        type: String,
        default: '',
      },
      loadingtext: {
        type: String,
        default: '',
      },
    },
    emits: ['changing'],
  };

  search = debounce(async () => {
    await fetch(`${this.$options.endpoint}?q=${this.value}`)
      .then((response) => {
        this.choices.clearChoices();
        this.choices.setChoices([{ label: this.$options.all, value: '-1' }]);
        return response.json();
      })
      .then((data) => {
        if (Object.keys(data).length > 0) {
          Object.values(data).forEach((item) => {
            this.choices.setChoices([item]);
          });
        } else {
          this.choices.setChoices([{ label: this.$options.all, value: '-1' }]);
        }
      });
  });

  // Create instance of Choices
  choices = new Choices(this.$el, {
    allowHTML: false,
    itemSelectText: '',
    searchEnabled: true,
    classNames: {
      containerOuter: 'select',
      containerInner: 'select__inner',
      input: 'select__input',
      inputCloned: 'select__input--cloned',
      list: 'select__list',
      listItems: 'select__list--multiple',
      listSingle: 'select__list--single',
      listDropdown: 'select__list--dropdown',
      item: 'select__item',
      itemSelectable: 'select__item--selectable',
      itemDisabled: 'select__item--disabled',
      itemOption: 'select__item--choice',
      group: 'select__group',
      groupHeading: 'select__heading',
      button: 'select__button',
      activeState: 'select--is-active',
      focusState: 'select--is-focused',
      openState: 'select--is-open',
      disabledState: 'select--is-disabled',
      highlightedState: 'select--is-highlighted',
      flippedState: 'select--is-flipped',
      selectedState: 'select--is-highlighted',
    },
    loadingText: this.$options.loadingtext,
    noResultsText: this.$options.resultstext,
    noChoicesText: this.$options.choicestext,
  });

  /**
   * @member {string} value
   */
  value = '';

  /**
   * Mounted hook
   */
  mounted() {
    this.choices.init();
  }

  /**
   * onSearch
   * @description this event is natively triggered by Choices.js
   * @param {Event} event
   */
  onSearch(event) {
    this.value = event.detail.value;
    this.search();
  }

  /**
   * onChange
   * @description send value to parent component
   */
  onChange() {
    this.$emit('changing', this.$el.id, this.choices.getValue());
  }

  /**
   * resetValue
   */
  resetValue() {
    this.choices.setChoiceByValue('-1');
  }

  /**
   * setValue
   * @param {number} value
   * @description used by parent only
   */
  setValue(value) {
    this.choices.setChoiceByValue(value);
    this.$emit('changing', this.$el.id, this.value);
  }
}
